import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Send, Lock, MessageCircleHeart, Heart } from "lucide-react";

const DRAWER = "/cassetto-giraffe.png";
const LIKED_KEY = "cassetto_liked";

const getLiked = () => {
  try { return new Set(JSON.parse(localStorage.getItem(LIKED_KEY) || "[]")); }
  catch { return new Set(); }
};
const saveLiked = (set) => localStorage.setItem(LIKED_KEY, JSON.stringify([...set]));

export default function IlCassetto() {
  const [question, setQuestion] = useState(undefined); // undefined=loading, null=none
  const [answers, setAnswers] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [liked, setLiked] = useState(getLiked());

  const loadAnswers = (qid) =>
    api.get(`/cassetto/answers/${qid}`).then((r) => setAnswers(r.data || [])).catch(() => {});

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/cassetto/question")
      .then((r) => {
        setQuestion(r.data || null);
        if (r.data) loadAnswers(r.data.id);
      })
      .catch(() => setQuestion(null));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const r = await api.post("/cassetto/answers", { question_id: question.id, text: t });
      setAnswers((a) => [...a, r.data]);
      setText("");
      toast.success("Grazie! La tua risposta anonima è stata condivisa 🦒");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore, riprova");
    } finally {
      setSending(false);
    }
  };

  const like = async (id) => {
    if (liked.has(id)) return;
    const next = new Set(liked); next.add(id); setLiked(next); saveLiked(next);
    setAnswers((a) => a.map((x) => (x.id === id ? { ...x, likes: (x.likes || 0) + 1 } : x)));
    try { await api.post(`/cassetto/answers/${id}/like`); }
    catch { /* keep optimistic */ }
  };

  return (
    <div className="relative z-10 min-h-screen" style={{ background: "linear-gradient(180deg,#FBF3E4 0%,#F7EAD3 40%,#F2E0C2 100%)" }}>
      <Header />

      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-14 pb-10 text-center">
        <p className="label-ui text-[#9F583F] mb-3">Il Cassetto</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#3A2415] leading-[1]">
          La giraffa ha una domanda per te
        </h1>

        {/* Drawer + giraffe + speech bubble */}
        <div className="relative mt-12 flex flex-col items-center">
          {question && (
            <div className="relative z-10 max-w-xl mb-[-10px]" data-testid="cassetto-question-bubble">
              <div className="bg-white rounded-3xl px-7 py-6 shadow-xl border-2 border-[#EADFC9]">
                <p className="font-display text-2xl md:text-3xl leading-snug text-[#1C1917]">
                  “{question.text}”
                </p>
              </div>
              <div className="w-6 h-6 bg-white border-b-2 border-r-2 border-[#EADFC9] rotate-45 mx-auto -mt-3" />
            </div>
          )}

          <img
            src={DRAWER}
            alt="Una giraffa che sbuca da un cassetto di legno"
            data-testid="cassetto-illustration"
            className="w-[300px] md:w-[380px] h-auto select-none pointer-events-none drop-shadow-2xl"
          />
        </div>

        {question === null && (
          <p className="mt-10 font-display text-2xl italic text-[#7A4A22]" data-testid="cassetto-no-question">
            La giraffa sta pensando a una nuova domanda… torna presto!
          </p>
        )}
      </section>

      {question && (
        <>
          {/* Answer form */}
          <section className="max-w-2xl mx-auto px-6 md:px-10 pb-4">
            <form onSubmit={submit} className="bg-white/80 backdrop-blur border-2 border-[#EADFC9] rounded-3xl p-6 md:p-8" data-testid="cassetto-answer-form">
              <label className="font-display text-xl text-[#3A2415] block mb-3">La tua risposta</label>
              <textarea
                data-testid="cassetto-answer-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="Scrivi qui ciò che pensi…"
                className="w-full font-body text-lg bg-[#FBFAF8] border border-[#E7E5E4] rounded-xl px-4 py-3 focus:outline-none focus:border-[#9F583F] transition-colors resize-none"
              />
              <div className="flex items-center justify-between gap-4 mt-4 flex-wrap">
                <span className="inline-flex items-center gap-2 label-ui text-[#8A7A63]">
                  <Lock size={13} strokeWidth={1.6} /> Del tutto anonimo — niente nome né email
                </span>
                <button
                  type="submit"
                  disabled={sending || !text.trim()}
                  data-testid="cassetto-answer-submit"
                  className="inline-flex items-center gap-2 bg-[#9F583F] hover:bg-[#1C1917] text-white label-ui px-6 py-3 rounded-full transition-colors disabled:opacity-50"
                >
                  {sending ? "Invio…" : "Condividi anonimamente"} <Send size={15} strokeWidth={1.7} />
                </button>
              </div>
            </form>
          </section>

          {/* Answers wall */}
          <section className="max-w-3xl mx-auto px-6 md:px-10 pb-24 pt-6">
            <div className="flex items-center gap-2 mb-6">
              <MessageCircleHeart size={18} strokeWidth={1.6} className="text-[#9F583F]" />
              <h2 className="font-display text-2xl text-[#3A2415]">
                Le risposte del cassetto {answers.length > 0 && <span className="text-[#A8895F]">({answers.length})</span>}
              </h2>
            </div>

            {answers.length === 0 ? (
              <p className="font-body italic text-[#7A4A22] text-center py-8" data-testid="cassetto-empty">
                Ancora nessuna risposta. Sii la prima giraffa a rispondere!
              </p>
            ) : (
              <div className="columns-1 sm:columns-2 gap-5 [column-fill:_balance]" data-testid="cassetto-answers-wall">
                {answers.map((a) => {
                  const isLiked = liked.has(a.id);
                  return (
                    <motion.div
                      key={a.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="break-inside-avoid mb-5 bg-white border border-[#EADFC9] rounded-2xl p-5"
                      data-testid={`cassetto-answer-${a.id}`}
                    >
                      <p className="font-body text-[#292524] leading-relaxed whitespace-pre-wrap">{a.text}</p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="label-ui text-[#B0997A]">🦒 Anonimo</span>
                        <button
                          onClick={() => like(a.id)}
                          disabled={isLiked}
                          data-testid={`cassetto-like-${a.id}`}
                          className={`inline-flex items-center gap-1.5 label-ui transition-colors ${isLiked ? "text-[#9F583F]" : "text-[#B0997A] hover:text-[#9F583F]"}`}
                          aria-label="Mi piace"
                        >
                          <Heart size={15} strokeWidth={1.8} fill={isLiked ? "#9F583F" : "none"} />
                          {a.likes > 0 && <span>{a.likes}</span>}
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      <Footer />
    </div>
  );
}
